import {
  Autocomplete,
  Box,
  BoxStatus,
  GraphicFilters,
  Label,
  PieCharts,
  SemiPieChart,
  Stack,
  StackRow,
  Select,
} from "codekit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  useGetGroupByIdMutation,
  useLazyGetDataGraphicCriticidQuery,
} from "../../../../libs/redux/slices/Graficsx/Criticid/criticidApiSlice";
import { useGetUrlRedirectCimaMutation } from "../../../../libs/redux/slices/Graficsx/graficsxApiSlice";
import { clearInitialFilter, getInitialFilter } from "../../../../libs/redux/slices/Graficsx/Criticid/criticidSlice";
import { useGetClientGraphicByIdMutation } from "../../../../libs/redux/slices/Reportxx/Repfinac/repfinacApiSlice";

const categories = {
  ROJO: "Muy Críticas",
  NARANJA: "Críticas",
  VERDE: "A tiempo",
};
const colors = ["ROJO", "NARANJA", "VERDE"];
const colorCategory = { "Muy Críticas": "error", Críticas: "warning", "A tiempo": "success" };
const colorsShades = [600, 500, 400, 300, 200, 100, 700];

/**
 * Grafica Criticidad de operaciones
 * @author Oscar Rosero <oscar.rosero@repremundo.com.co>
 * @since 2026-03-06
 */
const Criticid = () => {
  const dispatch = useDispatch();
  const initialFilter = useSelector(getInitialFilter);
  const { control, reset, setValue } = useForm({
    mode: "onBlur",
    defaultValues: { CLIIDXXX: "", USRJEFOG: "", COLORXXX: initialFilter ?? "" },
  });
  const [resultsClient, setResultsClient] = useState([]);
  const [getClientById] = useGetClientGraphicByIdMutation();
  const [resultsGroup, setResultsGroup] = useState([]);
  const [getGroupById] = useGetGroupByIdMutation();
  const [getDataGraphic] = useLazyGetDataGraphicCriticidQuery();
  const [isLoading, setIsLoading] = useState(true);
  const [dataGraphic, setDataGraphic] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [dataDetail, setDataDetail] = useState([]);
  const [colorsChart, setColorsChart] = useState([]);
  const [label, setLabel] = useState({ title: "", value: 0 });
  const [getUrlRedirectCima] = useGetUrlRedirectCimaMutation();
  const values = useWatch({ control });
  const showDetailRef = useRef(showDetail);
  const activeColorRef = useRef(initialFilter ?? "");

  // Autocompletar cliente
  const searchClient = async (CLIIDXXX, cb) => {
    return await getClientById({ filters: { CLIIDXXX } })
      .unwrap()
      .then((response) => setResultsClient(response.data))
      .catch((err) => console.log("searchClient error", err))
      .finally(() => cb());
  };

  // Autocompletar grupo
  const searchGroup = async (GRUPIDXX, cb) => {
    return await getGroupById({ filters: { GRUPIDXX } })
      .unwrap()
      .then((response) => setResultsGroup(response.data))
      .catch((err) => console.log("searchGroup error", err))
      .finally(() => cb());
  };

  // Mapear los datos de la grafica principal
  const mapDataGrphic = (data) => {
    return colors.map((color) => ({
      category: categories[color],
      value: data[color]?.total ?? 0,
      detail: data[color]?.estados ?? [],
    }));
  };

  // Retorna los colores para el pintado de la grafica
  const getColorByIndex = (category, index) => {
    const baseColor = colorCategory[category];
    const shade = colorsShades[index % colorsShades.length];
    return {
      colorCellChart: `${baseColor}-${shade}`,
      bgColorCards: `${baseColor}-100`,
      iconColor: `${baseColor}-400`,
      textColor: `${baseColor}-600`,
    };
  };

  // Mantener el ref sincronizado
  useEffect(() => {
    showDetailRef.current = showDetail;
  }, [showDetail]);

  useEffect(() => {
    activeColorRef.current = initialFilter ?? activeColorRef.current;
  }, [initialFilter]);

  // Construir el detalle a partir de un color y su data
  const buildDetail = useCallback((colorKey, colorData) => {
    const category = categories[colorKey];
    const detail = colorData.estados;
    return {
      label: { title: category, value: colorData.total },
      colorsChart: Object.keys(detail).map((_, index) => getColorByIndex(category, index)),
      dataDetail: Object.keys(detail).map((estado) => ({
        name: estado,
        value: detail[estado].total,
        operations: detail[estado].items.map((element) => element.DOCNROXX),
      })),
    };
  }, []);

  // Consulta de informacion de la Grafica
  const handleGetData = useCallback(
    async (values) => {
      let filters = {};
      if (values.CLIIDXXX !== "") {
        filters = { ...filters, "MDOSYYYY.CLIIDXXX": values.CLIIDXXX.split(" - ")[0] };
      }
      if (values.USRJEFOG !== "") {
        filters = { ...filters, "MDOSYYYY.USRJEFOG": values.USRJEFOG.split(" - ")[0] };
      }
      if (activeColorRef.current !== "") {
        filters = { ...filters, "MDSSYYYY.COLORXXX": activeColorRef.current };
      }
      await getDataGraphic({ filters })
        .unwrap()
        .then((response) => {
          const { data } = response;
          const dataMap = mapDataGrphic(data);
          setDataGraphic(dataMap);
          setIsLoading(false);
          if (showDetailRef.current && activeColorRef.current !== "") {
            const colorData = data[activeColorRef.current];
            if (colorData) {
              const built = buildDetail(activeColorRef.current, colorData);
              setLabel(built.label);
              setColorsChart(built.colorsChart);
              setDataDetail(built.dataDetail);
            } else {
              setDataDetail([]);
            }
            if (initialFilter) {
              const colorData = data[initialFilter];
              if (colorData) {
                const built = buildDetail(initialFilter, colorData);
                setShowDetail(true);
                setLabel(built.label);
                setColorsChart(built.colorsChart);
                setDataDetail(built.dataDetail);
              }
              dispatch(clearInitialFilter());
            }
          }
        })
        .catch();
    },
    [getDataGraphic, initialFilter, dispatch, buildDetail],
  );

  useEffect(() => {
    handleGetData(values);
  }, [handleGetData, values]);

  // Visualizacion del detalle al clickear una porcion del SemiPieChart
  const handleShowDetail = (context) => {
    const { detail, category, value } = context.data;
    setShowDetail(true);
    setLabel({ title: category, value });
    setColorsChart(Object.keys(detail).map((_, index) => getColorByIndex(category, index)));
    setDataDetail(
      Object.keys(detail).map((key) => ({
        name: key,
        value: detail[key].total,
        operations: detail[key].items.map((element) => element.DOCNROXX),
      })),
    );
  };

  // Redireccionamiento de operaciones
  const handleClickChart = async (context) => {
    const { operations = [] } = context.data;
    const payload = {
      MODIDXXX: "117",
      PROIDXXX: "100",
      [`filters[0][MDOSYYYY.DOCNROXX][IN]`]: operations,
    };
    await getUrlRedirectCima(payload)
      .unwrap()
      .then((response) => window.open(response.data, "_blank"))
      .catch();
  };

  // Verificar si el SemiPieChart tiene datos reales
  const hasDataGraphic = dataGraphic.some((item) => item.value > 0);

  //Limpiar filtros
  const handleResetFilters = () => {
    reset();
    setShowDetail(false);
    setValue("COLORXXX", "");
    activeColorRef.current = "";
  };

  return (
    <Box spacing={4}>
      <GraphicFilters
        containerName="Estado de criticidad de la operación"
        graphicKeyName="criticid"
        clearFilters={() => {
          handleResetFilters();
        }}
        onClickVerMas={() => {
          handleResetFilters();
        }}
      >
        <Box>
          <Box mb={8} borderBottom={1} spacing={2}>
            <Stack gap={3} columnsVariant="form-3">
              <StackRow>
                <Label>Estado</Label>
                <Controller
                  control={control}
                  name="COLORXXX"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      value={value}
                      options={[
                        { text: "SELECCIONE", value: "" },
                        { text: "Muy Críticas", value: "ROJO" },
                        { text: "Críticas", value: "NARANJA" },
                        { text: "A tiempo", value: "VERDE" },
                      ]}
                      onChangeValue={(context) => {
                        onChange(context.option.value);
                        activeColorRef.current = context.option.value;
                        if (showDetail && context.option.value !== "") {
                          setShowDetail(true);
                        } else if (context.option.value === "") {
                          setShowDetail(false);
                        }
                      }}
                    />
                  )}
                />
              </StackRow>
              <StackRow>
                <Label>Cliente</Label>
                <Controller
                  control={control}
                  name="CLIIDXXX"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      placeholder="Buscar..."
                      textLoading="Buscando..."
                      textNoResults="No se encontraron registros"
                      minLengthToSearch={2}
                      selected={value}
                      searchResults={resultsClient}
                      onChangeSearch={(value, cb) => searchClient(value, cb)}
                      onItemClick={(context) => onChange(`${context.item.label}`)}
                      onResetSelected={() => onChange("")}
                    />
                  )}
                />
              </StackRow>
              <StackRow>
                <Label>Grupo de Trabajo</Label>
                <Controller
                  control={control}
                  name="USRJEFOG"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      placeholder="Buscar..."
                      textLoading="Buscando..."
                      textNoResults="No se encontraron registros"
                      minLengthToSearch={2}
                      selected={value}
                      searchResults={resultsGroup}
                      onChangeSearch={(value, cb) => searchGroup(value, cb)}
                      onItemClick={(context) => onChange(`${context.item.label}`)}
                      onResetSelected={() => onChange("")}
                    />
                  )}
                />
              </StackRow>
            </Stack>
          </Box>
        </Box>
        {isLoading ? (
          <BoxStatus variant="loading" text="Cargando Información..." />
        ) : (
          <>
            {!showDetail ? (
              hasDataGraphic ? (
                <SemiPieChart
                  handleLabelClick={(context) => handleShowDetail(context)}
                  data={dataGraphic}
                  chartColors={[
                    { colorCellChart: "error-400" },
                    { colorCellChart: "warning-400" },
                    { colorCellChart: "success-400" },
                  ]}
                />
              ) : (
                <BoxStatus variant="empty" text="Sin Información" />
              )
            ) : dataDetail.length > 0 ? (
              <PieCharts
                onClickCellChart={(context) => handleClickChart(context)}
                onClickIconCard={(context) => handleClickChart(context)}
                data={dataDetail}
                chartColors={colorsChart}
                labelOne={label.title}
                labelTwo={label.value}
              />
            ) : (
              <BoxStatus variant="empty" text="Sin Información" />
            )}
          </>
        )}
      </GraphicFilters>
    </Box>
  );
};

export default Criticid;
